import { createSelector } from 'reselect'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { getFullOffers } from 'selectors/offerSelectors'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'

/**
 * Calculate the payment schedule adjustment amount for hotel items
 * This is used to determine the adjustment amount BEFORE making the API call
 */
const getPaymentScheduleAdjustmentAmount = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.mode,
  (
    existingOrder,
    cartItems,
    offers,
    calendarsByOccupancy,
    availableRatesByOccupancy,
    checkoutWithMemberPrice,
    cartMode,
  ): number => {
    // Default to 0 if conditions aren't met
    if (!existingOrder) {
      return 0
    }

    const hotelItem = cartItems.find(isInstantBookingLEHotelItem)

    if (!hotelItem?.orderItemId) {
      return 0
    }

    const orderItem = existingOrder.items.find(item => item.id === hotelItem.orderItemId)
    const offer = offers[hotelItem.offerId]

    if (!orderItem || offer?.type !== 'hotel') {
      return 0
    }

    const originPackage = offer.packages.find(
      pkg => pkg.id === hotelItem.packageId && pkg.duration === hotelItem.duration,
    )

    if (!originPackage) {
      return 0
    }

    // Calculate new totals using the same logic as getLEPackageItemView
    const itemTotals = getLEPackageItemTotals(
      offer,
      originPackage,
      hotelItem.checkIn,
      hotelItem.checkOut,
      hotelItem.occupancy,
      undefined,
      calendarsByOccupancy,
      availableRatesByOccupancy,
    )

    if (!itemTotals) {
      return 0
    }

    // Apply any overrides from the cart item
    let price = itemTotals.price
    let surcharge = itemTotals.surcharge

    if (hotelItem.newPrice !== undefined) {
      price = hotelItem.newPrice
    }
    if (hotelItem.newSurcharge !== undefined) {
      surcharge = hotelItem.newSurcharge + (itemTotals.extraGuestSurcharge ?? 0)
    }
    if (hotelItem.newExtraGuestSurcharge !== undefined) {
      itemTotals.extraGuestSurcharge = hotelItem.newExtraGuestSurcharge
    }

    // Calculate new price based on cart mode
    let newPrice = 0

    if (cartMode === 'select-date') {
      // For BNBL select-date: preserve original package price
      const priceToUse = orderItem.packagePrice
      const memberPriceToUse = orderItem.packagePrice
      const nextPrice = checkoutWithMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse
      newPrice = nextPrice + ((itemTotals.isAvailable ? surcharge : orderItem.reservation?.surcharge) || 0)
    } else if (cartMode === 'change-dates' || cartMode === 'add-extra-guests') {
      // For change-dates/add-extra-guests
      const newPackageSelected = orderItem.package.duration !== hotelItem.duration || orderItem.package.uniqueKey !== originPackage.uniqueKey
      let priceToUse = newPackageSelected ? price : orderItem.packagePrice
      if (orderItem.channelMarkupId) {
        priceToUse = price
      }
      const memberPriceToUse = newPackageSelected ? itemTotals.memberPrice : orderItem.packagePrice
      const nextPrice = checkoutWithMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse
      newPrice = nextPrice + ((itemTotals.isAvailable ? surcharge : orderItem.reservation?.surcharge) || 0)
    } else if (hotelItem.isUpdated || cartMode === 'change-package') {
      // For change-package and other updated items
      const nextPrice = checkoutWithMemberPrice && itemTotals.memberPrice > 0 ? itemTotals.memberPrice : price
      newPrice = nextPrice + (surcharge || 0)
    }

    // Calculate the adjustment amount (only for modes that trigger payment schedule adjustments)
    if (cartMode === 'select-date' || cartMode === 'change-dates' || cartMode === 'add-extra-guests' || cartMode === 'change-package') {
      return newPrice - orderItem.total
    }

    return 0
  },
)

export default getPaymentScheduleAdjustmentAmount
